import { h, Component } from 'preact';
import { route } from 'preact-router';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import util from 'preact-util';

import localUtil from '../../lib/util';
import { searchRaces, raceRoute, isRaceDomain } from '../../lib/raceSearch';

function categoryIcon(category) {
    switch (category) {
        case 'help': return 'fa-solid fa-circle-question';
        case 'feed': return 'fa-solid fa-comments';
        case 'news': return 'fa-solid fa-newspaper';
        case 'marketplace': return 'fa-solid fa-store';
        default: return 'fa-solid fa-circle-info';
    }
}

function articleRoute(result) {
    const { category, id, uuidv4, title } = result;
    switch (category) {
        case 'help':
            return `/help/${id}`;
        case 'feed':
            return `/feed/${uuidv4}`;
        case 'marketplace':
            return `/marketplace/${id}`;
        default: {
            const slug = encodeURIComponent(title || '').toLocaleLowerCase();
            return `/article/${slug}/${uuidv4}`;
        }
    }
}

function categoryLabel(category) {
    switch (category) {
        case 'help': return 'Help';
        case 'feed': return 'Feed';
        case 'news': return 'News';
        case 'marketplace': return 'Marketplace';
        case 'race': return 'Race';
        default:
            if (!category) return 'Article';
            return category.charAt(0).toUpperCase() + category.slice(1);
    }
}

@observer
class QuickSearch extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            query: '',
            results: [],
            selectedIndex: 0,
            loading: false,
        };
        this.searchInputRef = null;
    }

    componentDidMount() {
        // Add keyboard shortcut listener (Cmd+K or Ctrl+K or /)
        document.addEventListener('keydown', this.handleGlobalKeyPress);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleGlobalKeyPress);
    }

    handleGlobalKeyPress = (e) => {
        // Cmd+K or Ctrl+K
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            this.toggleSearch();
        }
        // Forward slash (only if not typing in an input)
        if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
            e.preventDefault();
            this.toggleSearch();
        }
        // Escape key to close
        if (e.key === 'Escape' && this.state.isOpen) {
            this.closeSearch();
        }
    }

    toggleSearch = () => {
        this.setState({ isOpen: !this.state.isOpen }, () => {
            if (this.state.isOpen && this.searchInputRef) {
                this.searchInputRef.focus();
            }
        });
    }

    closeSearch = () => {
        this.setState({
            isOpen: false,
            query: '',
            results: [],
            selectedIndex: 0,
        });
    }

    handleSearch = async (e) => {
        const query = e.target.value;
        this.setState({ query, selectedIndex: 0 });

        if (!query || query.length < 2) {
            this.setState({ results: [] });
            return;
        }

        // Check if it's a command (e.g., "sale 1234")
        const commandMatch = query.match(/^(\w+)\s+(\d+)$/i);
        if (commandMatch) {
            const [, command, id] = commandMatch;
            this.setState({
                results: [{
                    type: 'command',
                    command: command.toLowerCase(),
                    id: parseInt(id, 10),
                    title: `Open ${command} #${id}`,
                    icon: 'fa-solid fa-arrow-right',
                }]
            });
            return;
        }

        // The default (top) action funnels the query into the Help Center, where the
        // article search AND the AI answer run. It stays highlighted (selectedIndex 0),
        // so pressing Enter opens the Help Center; the article rows below are quick jumps.
        const helpSearchRow = { type: 'helpSearch', title: query, icon: 'fa-solid fa-wand-magic-sparkles' };

        // Search help articles, and races on race domains (title words, max 5).
        this.setState({ loading: true });
        try {
            const { articleStore, appState, userStore } = this.props.stores;
            const [articles, races] = await Promise.all([
                articleStore.load({
                    query: {
                        hostname: articleStore.currentHostname,
                        search: query,
                        all: 1,
                    },
                    skipUpdate: true,
                }),
                searchRaces({ search: query, currentCustomer: appState.currentCustomer }),
            ]);

            const locale = userStore.language === 'en' ? 'en-GB' : 'nb-NO';
            const raceResults = races.map(race => ({
                type: 'race',
                id: race.id,
                category: 'race',
                title: race.title,
                description: [
                    race.startDate ? localUtil.formatDate(race.startDate, { locale, dateStyle: 'medium' }) : '',
                    race.isCancelled ? 'Avlyst / Cancelled' : '',
                ].filter(Boolean).join(' · '),
                icon: 'fa-regular fa-flag-checkered',
            }));

            if (articles && Array.isArray(articles)) {
                const articleResults = articles.map(article => ({
                    type: 'article',
                    id: article.id,
                    uuidv4: article.uuidv4,
                    category: article.category || 'article',
                    title: article.title,
                    description: article.ingress || article.body?.substring(0, 150),
                    icon: article.headingIcon || categoryIcon(article.category),
                    hasVideo: article.files?.some(f => f.mimetype?.startsWith('video/')),
                    hasSubtitles: article.files?.some(f =>
                        f.mimetype?.startsWith('video/') &&
                        f.transcript &&
                        f.transcript.length > 0
                    ),
                }));
                this.setState({ results: [helpSearchRow, ...raceResults, ...articleResults], loading: false });
            } else {
                this.setState({ results: [helpSearchRow, ...raceResults], loading: false });
            }
        } catch (error) {
            console.error('Search error:', error);
            this.setState({ results: [helpSearchRow], loading: false });
        }
    }

    handleKeyDown = (e) => {
        const { selectedIndex, results } = this.state;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({
                selectedIndex: Math.min(selectedIndex + 1, results.length - 1)
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({
                selectedIndex: Math.max(selectedIndex - 1, 0)
            });
        } else if (e.key === 'Enter' && results[selectedIndex]) {
            e.preventDefault();
            this.selectResult(results[selectedIndex]);
        }
    }

    selectResult = (result) => {
        if (result.type === 'command') {
            // Handle commands
            const { command, id } = result;
            this.closeSearch();

            // Route based on command type
            switch (command) {
                case 'sale':
                    route(`/dyrejournal-sale/${id}`);
                    break;
                case 'journal':
                    route(`/dyrejournal/journal/${id}`);
                    break;
                case 'animal':
                    route(`/dyrejournal/animal/${id}`);
                    break;
                case 'visitor':
                    route(`/dyrejournal/visitor/${id}`);
                    break;
                case 'product':
                    route(`/dyrejournal/product/${id}`);
                    break;
                default:
                    console.log('Unknown command:', command);
            }
        } else if (result.type === 'helpSearch') {
            // Funnel into the Help Center, carrying the query so its article search + AI answer run.
            const query = result.title;
            this.closeSearch();
            route(`/help/search/${encodeURIComponent(query)}`);
        } else if (result.type === 'race') {
            this.closeSearch();
            route(raceRoute(result));
        } else if (result.type === 'article') {
            this.closeSearch();
            route(articleRoute(result));
        }
    }

    handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            this.closeSearch();
        }
    }

    render() {
        const { isOpen, query, results, selectedIndex, loading } = this.state;
        const { appState } = this.props.stores;
        const hasRaces = isRaceDomain(appState?.currentCustomer);

        return (<>
            {/* Search overlay */}
            {isOpen && (
                <div
                    class='position-fixed top-0 start-0 w-100 h-100'
                    style='background-color: rgba(0, 0, 0, 0.5); z-index: 10002; backdrop-filter: blur(4px);'
                    onClick={this.handleOverlayClick}
                >
                    <div
                        class='container'
                        style='margin-top: 10vh; max-width: 600px;'
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Search input */}
                        <div class='card shadow-lg'>
                            <div class='card-body p-0'>
                                <div class='input-group input-group-lg'>
                                    <span class='input-group-text border-0 bg-white'>
                                        <i class='fa-solid fa-magnifying-glass' />
                                    </span>
                                    <Localizer>
                                        <input
                                            ref={ref => this.searchInputRef = ref}
                                            type='text'
                                            class='form-control border-0'
                                            placeholder={hasRaces
                                                ? <Text id='quickSearch.placeholder-races'>Search articles, help, feed, races...</Text>
                                                : <Text id='quickSearch.placeholder'>Search articles, help, feed...</Text>}
                                            value={query}
                                            onInput={this.handleSearch}
                                            onKeyDown={this.handleKeyDown}
                                            autoFocus
                                        />
                                    </Localizer>
                                    <button
                                        type='button'
                                        class='btn btn-link text-muted'
                                        onClick={this.closeSearch}
                                    >
                                        <i class='fa-solid fa-xmark' />
                                    </button>
                                </div>

                                {/* Results */}
                                {results.length > 0 && (
                                    <div class='border-top'>
                                        {results.map((result, index) => (
                                            <div
                                                key={`result-${index}`}
                                                class={`p-3 border-bottom cursor-pointer ${
                                                    index === selectedIndex ? 'bg-light' : ''
                                                }`}
                                                style='transition: background-color 0.15s;'
                                                onClick={() => this.selectResult(result)}
                                                onMouseEnter={() => this.setState({ selectedIndex: index })}
                                            >
                                                <div class='d-flex align-items-start'>
                                                    <div class='me-3 mt-1'>
                                                        <i
                                                            class={result.icon}
                                                            style={`color: ${(result.type === 'command' || result.type === 'helpSearch') ? '#0d6efd' : '#6c757d'}; font-size: 1.2rem;`}
                                                        />
                                                    </div>
                                                    <div class='flex-grow-1'>
                                                        {result.type === 'helpSearch' ? (
                                                            <div class='d-flex align-items-center gap-2 flex-wrap'>
                                                                <span class='fw-bold'>
                                                                    <Text id='quickSearch.searchInHelpCenter' fields={{ query: result.title }}>
                                                                        Search "{result.title}" in Help Center
                                                                    </Text>
                                                                </span>
                                                                <span class='badge text-bg-primary'>AI</span>
                                                            </div>
                                                        ) : (<>
                                                        <div class='d-flex align-items-center gap-2 flex-wrap'>
                                                            <span class='fw-bold'>{result.title}</span>
                                                            {result.category && (
                                                                <span class='badge text-bg-secondary'>{categoryLabel(result.category)}</span>
                                                            )}
                                                        </div>
                                                        {result.description && (
                                                            <div class='text-muted small mt-1'>
                                                                {result.description}
                                                            </div>
                                                        )}
                                                        {result.hasVideo && (
                                                            <div class='mt-1'>
                                                                <i class='fa-solid fa-film text-muted me-2' style='font-size: 0.85rem;' />
                                                                {result.hasSubtitles && (
                                                                    <i class='fa-solid fa-closed-captioning text-success' style='font-size: 0.85rem;' />
                                                                )}
                                                            </div>
                                                        )}
                                                        </>)}
                                                    </div>
                                                    <div class='ms-2 text-muted'>
                                                        {index === selectedIndex && (
                                                            <small>↵</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Loading indicator */}
                                {loading && (
                                    <div class='p-4 text-center text-muted'>
                                        <i class='fa-solid fa-spinner fa-spin me-2' />
                                        Searching...
                                    </div>
                                )}

                                {/* No results */}
                                {!loading && query.length >= 2 && results.length === 0 && (
                                    <div class='p-4 text-center text-muted'>
                                        <i class='fa-solid fa-circle-info me-2' />
                                        No results found
                                    </div>
                                )}

                                {/* Hints */}
                                {/* {!query && (
                                    <div class='p-3 border-top bg-light'>
                                        <small class='text-muted'>
                                            <div class='mb-2'><strong>Quick commands:</strong></div>
                                            <div class='mb-1'><kbd>sale 1234</kbd> - Open sale</div>
                                            <div class='mb-1'><kbd>journal 1234</kbd> - Open journal</div>
                                            <div class='mb-1'><kbd>animal 1234</kbd> - Open animal</div>
                                            <div class='mb-1'><kbd>visitor 1234</kbd> - Open visitor</div>
                                            <div class='mb-1'><kbd>product 1234</kbd> - Open product</div>
                                            <hr class='my-2' />
                                            <div><kbd>↑</kbd> <kbd>↓</kbd> navigate <kbd>↵</kbd> select <kbd>Esc</kbd> close</div>
                                        </small>
                                    </div>
                                )} */}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>);
    }
}

export default QuickSearch;
